/**
 * StaffTab — вкладка «Сотрудники» (только клиника): единый список админ +
 * врачи, «⋯» на строке (изменить/удалить врача, заблокировать аккаунт),
 * пометки «Исключён» и «Заблокирован», «Добавить врача» и апселл при
 * заполненных местах (ADMIN_SPEC §3.5).
 */

import {CircleAlert, Lock, MoreVertical, Pencil, Plus, Trash2, Unlock} from 'lucide-react';

import type {ClientDetail, ClientListItem, StaffMember} from '@services/clients';
import {Button} from '@components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/DropdownMenu';
import {cn} from '@lib/utils';

/**
 * Кого блокируем — аккаунт человека, а не членство. Берём из самого
 * сотрудника: бэк кладёт `accountId` в карточку, и отдельный запрос
 * `admin/accounts?clinicId=`, которым мы это раньше добывали, больше не
 * нужен. Пока он был, меню действий молча пропадало, если тот запрос не
 * успел прийти.
 */
export type StaffAccountRef = {id: string; fullName: string};

const accountRefOf = (m: StaffMember): StaffAccountRef | null =>
  m.accountId ? {id: m.accountId, fullName: m.fullName} : null;

const initials = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

type StaffTabProps = {
  client: ClientListItem;
  detail: ClientDetail;
  onAddDoctor: () => void;
  onEditDoctor: (d: StaffMember) => void;
  onDeleteDoctor: (d: StaffMember) => void;
  /** Заблокировать аккаунт человека целиком (все клиники) */
  onBlockAccount: (a: StaffAccountRef) => void;
  onUnblockAccount: (a: StaffAccountRef) => void;
};

export const StaffTab = ({
  client,
  detail,
  onAddDoctor,
  onEditDoctor,
  onDeleteDoctor,
  onBlockAccount,
  onUnblockAccount,
}: StaffTabProps) => {
  const limit = client.seatsLimit ?? 0;
  const full = client.seatsUsed >= limit;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Сотрудники</div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground">
            Занято {client.seatsUsed} из {limit} мест · админ бесплатно
          </div>
        </div>
        <Button size="sm" className="h-9 gap-1.5" disabled={full} onClick={onAddDoctor}>
          <Plus className="size-[15px]" strokeWidth={2.3} />
          Добавить врача
        </Button>
      </div>

      {full && (
        <div className="flex gap-[11px] rounded-[13px] border border-[#F1E4C9] bg-[#FBF3E4] p-[14px_16px]">
          <CircleAlert className="mt-px size-[19px] shrink-0 text-[#B07908]" strokeWidth={1.9} />
          <div>
            <div className="text-[13.5px] font-semibold text-[#8A6410]">Все места заняты</div>
            <div className="mt-0.5 text-[12.5px] leading-[1.45] text-[#9A7B36]">
              Занято {client.seatsUsed} из {limit}. Чтобы добавить врача, поднимите лимит мест на
              следующую ступень тарифа.
            </div>
            <button className="mt-2.5 h-[34px] rounded-lg border border-[#EBD9AE] bg-card px-[13px] text-[12.5px] font-semibold text-[#8A6410]">
              Поднять лимит мест
            </button>
          </div>
        </div>
      )}

      <div className="rounded-[13px] border border-skr-line bg-card">
        {detail.staff.map((m, i) => (
          <div
            key={m.id}
            className={`flex items-center gap-3 px-4 py-[13px] ${i < detail.staff.length - 1 ? 'border-b border-skr-hairline' : ''}`}
          >
            <span
              className={
                m.role === 'admin'
                  ? 'grid size-[38px] shrink-0 place-items-center rounded-full bg-accent text-[13px] font-semibold text-primary'
                  : 'grid size-[38px] shrink-0 place-items-center rounded-full bg-skr-icon-bg text-[13px] font-semibold text-muted-foreground'
              }
            >
              {initials(m.fullName)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className={cn('text-sm font-semibold', !m.active && 'text-skr-faint')}>
                  {m.fullName}
                </span>
                {!m.active && <Tag tone="muted">Исключён</Tag>}
                {m.blocked && <Tag tone="danger">Заблокирован</Tag>}
              </div>
              <div className="text-xs text-muted-foreground">
                {m.role === 'admin' ? (
                  <span className="font-mono">{m.phone}</span>
                ) : (
                  `Врач · ${m.specialization ?? '—'}`
                )}
              </div>
            </div>
            {m.role === 'admin' && (
              <span className="rounded-full bg-accent px-[9px] py-[3px] text-[11.5px] font-semibold text-primary">
                Админ
              </span>
            )}
            <StaffMenu
              member={m}
              account={accountRefOf(m)}
              onEdit={onEditDoctor}
              onDelete={onDeleteDoctor}
              onBlock={onBlockAccount}
              onUnblock={onUnblockAccount}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── локальные части ───────────────────────────────────────────── */

const Tag = ({tone, children}: {tone: 'muted' | 'danger'; children: string}) => (
  <span
    className={cn(
      'rounded-full px-[7px] py-px text-[10.5px] font-semibold',
      tone === 'danger' ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-skr-faint',
    )}
  >
    {children}
  </span>
);

const StaffMenu = ({
  member: m,
  account,
  onEdit,
  onDelete,
  onBlock,
  onUnblock,
}: {
  member: StaffMember;
  account: StaffAccountRef | null;
  onEdit: (d: StaffMember) => void;
  onDelete: (d: StaffMember) => void;
  onBlock: (a: StaffAccountRef) => void;
  onUnblock: (a: StaffAccountRef) => void;
}) => {
  const doctor = m.role === 'doctor' && m.active;
  if (!doctor && account === null) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Действия"
        className="grid size-[30px] place-items-center rounded-md text-skr-faint outline-none hover:text-muted-foreground"
      >
        <MoreVertical className="size-[17px]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[214px]">
        {doctor && (
          <>
            <DropdownMenuItem onClick={() => onEdit(m)}>
              <Pencil />
              Изменить данные
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete(m)}
            >
              <Trash2 />
              Удалить врача
            </DropdownMenuItem>
          </>
        )}
        {doctor && account !== null && <DropdownMenuSeparator />}
        {account !== null &&
          (m.blocked ? (
            <DropdownMenuItem onClick={() => onUnblock(account)}>
              <Unlock />
              Разблокировать аккаунт
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onBlock(account)}
            >
              <Lock />
              Заблокировать аккаунт
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
